// One gentle mobile swipe cue per product visit; the selected slide never changes.
const hintedProducts = new Set();

class KezGalleryHint extends HTMLElement {
  connectedCallback() {
    this.gallery = this.closest('media-gallery');
    this.scroller = this.gallery?.querySelector('slideshow-slides');
    if (!this.scroller || hintedProducts.has(this.dataset.productId)) return;

    this.mobile = matchMedia('(max-width: 749px)');
    this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    this.listeners = new AbortController();
    this.animations = [];
    this.visible = false;
    this.done = false;
    const options = { signal: this.listeners.signal, passive: true };
    for (const type of ['pointerdown', 'touchstart', 'keydown', 'wheel']) {
      this.gallery.addEventListener(type, this.dismiss, options);
    }
    this.scroller.addEventListener('scroll', this.dismiss, options);
    this.gallery.addEventListener('slideshow:select', event => {
      if (event.detail?.userInitiated) this.dismiss();
    }, options);
    document.addEventListener('visibilitychange', this.schedule, options);
    this.mobile.addEventListener('change', this.schedule, options);
    this.reducedMotion.addEventListener('change', this.schedule, options);

    this.observer = new IntersectionObserver(([entry]) => {
      this.visible = entry.isIntersecting && entry.intersectionRatio >= 0.65;
      this.schedule();
    }, { threshold: [0, 0.65] });
    this.observer.observe(this.scroller);
  }

  disconnectedCallback() {
    this.stop();
    this.listeners?.abort();
    this.observer?.disconnect();
  }

  stop = () => {
    clearTimeout(this.timer);
    this.animations?.forEach(animation => animation.cancel());
    this.animations = [];
  };

  dismiss = () => {
    this.done = true;
    hintedProducts.add(this.dataset.productId);
    this.dataset.state = 'dismissed';
    this.stop();
    this.observer?.disconnect();
  };

  schedule = () => {
    this.stop();
    if (this.done || !this.visible || !this.mobile.matches || this.reducedMotion.matches || document.hidden) return;
    const photo = this.scroller.querySelector('slideshow-slide:not([hidden]) img');
    if (!photo?.complete || !photo.naturalWidth) {
      photo?.addEventListener('load', this.schedule, { once: true, signal: this.listeners.signal });
      return;
    }
    this.dataset.state = 'waiting';
    this.timer = setTimeout(this.nudge, 3000);
  };

  nudge = () => {
    if (this.done || !this.isConnected || !this.visible || !this.mobile.matches || this.reducedMotion.matches || document.hidden) return;
    const slides = [...this.scroller.querySelectorAll('slideshow-slide:not([hidden])')];
    if (slides.length < 2 || Math.abs(this.scroller.scrollLeft) > 4) return;

    this.done = true;
    hintedProducts.add(this.dataset.productId);
    this.dataset.state = 'nudged';
    this.observer.disconnect();
    // Move the slides together inside the existing viewport to reveal a little
    // of the next photo. No scroll or slideshow.select call changes selection.
    const offset = getComputedStyle(this.scroller).direction === 'rtl' ? '24px' : '-24px';
    this.animations = slides.map(slide => slide.animate([
      { transform: 'translateX(0)', offset: 0 },
      { transform: `translateX(${offset})`, offset: 0.45 },
      { transform: `translateX(${offset})`, offset: 0.6 },
      { transform: 'translateX(0)', offset: 1 },
    ], { duration: 850, easing: 'ease-in-out' }));
  };
}

if (!customElements.get('kez-gallery-hint')) {
  customElements.define('kez-gallery-hint', KezGalleryHint);
}
